import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../shell/TenantContext';
import { useUiV2 } from '../shell/uiFlags';
import { meetsPasswordPolicy } from '../lib/passwordPolicy';
import MaicBrand from '../components/MaicBrand';
import { v1OrClass } from './publicUi';
import './public.css';

// `v2` strokes with the text color of its field instead of the fixed slate.
const LockIcon: React.FC<{ v2?: boolean }> = ({ v2 }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={v2 ? 'currentColor' : '#5B6B85'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ChangePasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, changePassword, logout } = useAuth();
  const [currentPass, setCurrentPass] = useState('');
  const [currentFocused, setCurrentFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [newFocused, setNewFocused] = useState(false);
  const [confirmFocused, setConfirmFocused] = useState(false);
  // Shown after sign-in, so the tenant is known: the tenant flag decides
  // (unlike the sign-in page, pages/publicUi.ts). With it, the sign-in card
  // of public.css with the theme tokens instead of the inline colors.
  const v2 = useUiV2();
  const sx = (v1: React.CSSProperties, className: string) => v1OrClass(v2, v1, className);

  const W = 210;

  const inputStyle = (focused: boolean): React.CSSProperties => ({
    width: W, height: 36,
    backgroundColor: '#1F2D3D',
    border: `1px solid ${focused ? '#4F6BC6' : '#2D3F55'}`,
    borderRadius: 3, color: '#C5CDD8', fontSize: 13,
    padding: '0 10px 0 32px', outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box', caretColor: '#C5CDD8',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPass) { setError(t('auth.currentPasswordRequired', 'Ingresa la contraseña temporal que te dieron.')); return; }
    if (!meetsPasswordPolicy(newPass)) { setError(t('auth.passwordPolicy', 'La contraseña debe tener al menos 12 caracteres, con mayúscula, minúscula, número y símbolo.')); return; }
    if (newPass !== confirm) { setError(t('auth.passwordsDoNotMatch', 'Las contraseñas no coinciden.')); return; }
    if (newPass === currentPass) { setError(t('auth.passwordsMustDiffer', 'La nueva contraseña debe ser distinta de la temporal.')); return; }
    if (!currentUser) return;
    setSaving(true);
    const result = await changePassword(currentPass, newPass);
    setSaving(false);
    // On success the session now carries no "temporary" flag and the shell
    // leaves this page by itself.
    if (!result.success) {
      const detail = result.error || '';
      setError(
        detail === 'Invalid credentials'
          ? t('auth.currentPasswordWrong', 'La contraseña temporal no es correcta. Después de varios intentos fallidos la cuenta se bloquea unos minutos.')
          : t('auth.changePasswordFailed', 'No pudimos cambiar la contraseña. Intenta de nuevo.'),
      );
    }
  };

  return (
    <div {...sx({
      minHeight: '100vh', backgroundColor: '#1B2333',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif',
      backgroundImage: 'radial-gradient(ellipse at 50% 40%, #1E2A3D 0%, #151D2B 100%)',
    }, 'pub-page')}>
      <div {...sx({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }, 'pub-card')}>

        {/* Header text */}
        {v2 ? (
          <>
            <MaicBrand className="pub-brand" />
            <h1 className="pub-title">{t('auth.setPassword', 'Set your password')}</h1>
            {/* auth.setPasswordDesc / setPasswordBtn have the Spanish text the
                v1 keys (tempPasswordAssigned, setPasswordButton) lack. */}
            <p className="pub-lead">{t('auth.setPasswordDesc', 'A temporary password was assigned to your account. Create a new one to continue.')}</p>
          </>
        ) : (
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#8A9BAE', marginBottom: 4 }}>
            {t('auth.setPassword', 'Set your password')}
          </div>
          <div style={{ fontSize: 11, color: '#4A5568', maxWidth: W }}>
            {t('auth.tempPasswordAssigned', 'A temporary password was assigned to your account. Create a new one to continue.')}
          </div>
        </div>
        )}

        <form onSubmit={handleSubmit} {...sx({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }, 'pub-form')}>

          {/* Signed in as */}
          <div {...sx({ width: W, fontSize: 11, color: '#4A5568', textAlign: 'center', marginBottom: 2 }, 'pub-who')}>
            {t('auth.signedInAs', 'Signed in as')} <span {...sx({ color: '#8A9BAE' }, 'pub-who__email')}>{currentUser?.email}</span>
          </div>

          {/* Current (temporary) password */}
          <div {...sx({ position: 'relative', width: W }, 'pub-field')}>
            <div {...sx({ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', lineHeight: 0, pointerEvents: 'none' }, 'pub-field__icon')}>
              <LockIcon v2={v2} />
            </div>
            <input
              type="password"
              value={currentPass}
              onChange={(e) => { setCurrentPass(e.target.value); setError(''); }}
              placeholder={t('auth.currentPasswordTemp', 'Contraseña temporal')}
              autoComplete="current-password"
              autoFocus
              {...sx(inputStyle(currentFocused), 'pub-input')}
              onFocus={() => setCurrentFocused(true)}
              onBlur={() => setCurrentFocused(false)}
            />
          </div>

          {/* New password */}
          <div {...sx({ position: 'relative', width: W }, 'pub-field')}>
            <div {...sx({ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', lineHeight: 0, pointerEvents: 'none' }, 'pub-field__icon')}>
              <LockIcon v2={v2} />
            </div>
            <input
              type="password"
              value={newPass}
              onChange={(e) => { setNewPass(e.target.value); setError(''); }}
              placeholder={t('auth.newPassword', 'New password')}
              autoComplete="new-password"
              {...sx(inputStyle(newFocused), 'pub-input')}
              onFocus={() => setNewFocused(true)}
              onBlur={() => setNewFocused(false)}
            />
          </div>

          {/* Confirm password */}
          <div {...sx({ position: 'relative', width: W }, 'pub-field')}>
            <div {...sx({ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', lineHeight: 0, pointerEvents: 'none' }, 'pub-field__icon')}>
              <LockIcon v2={v2} />
            </div>
            <input
              type="password"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setError(''); }}
              placeholder={t('auth.confirmPassword', 'Confirm password')}
              autoComplete="new-password"
              {...sx(inputStyle(confirmFocused), 'pub-input')}
              onFocus={() => setConfirmFocused(true)}
              onBlur={() => setConfirmFocused(false)}
            />
          </div>

          {/* Strength hint */}
          {newPass.length > 0 && v2 && (
            <div className="pub-meter" data-strength={newPass.length >= 12 ? 4 : newPass.length >= 8 ? 3 : newPass.length >= 6 ? 2 : 1}>
              {[0, 1, 2, 3].map((i) => <div key={i} className="pub-meter__seg" />)}
            </div>
          )}
          {newPass.length > 0 && !v2 && (
            <div style={{ width: W, display: 'flex', gap: 4 }}>
              {[...Array(4)].map((_, i) => {
                const strength = newPass.length >= 12 ? 4 : newPass.length >= 8 ? 3 : newPass.length >= 6 ? 2 : 1;
                const colors = ['#EF4444', '#F97316', '#EAB308', '#22C55E'];
                return (
                  <div key={i} style={{
                    flex: 1, height: 2, borderRadius: 1,
                    backgroundColor: i < strength ? colors[strength - 1] : '#1F2D3D',
                    transition: 'background-color 200ms',
                  }} />
                );
              })}
            </div>
          )}

          {error && (
            <div {...sx({ width: W, fontSize: 11, color: '#F87171', textAlign: 'center' }, 'pub-error')} role={v2 ? 'alert' : undefined}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            {...sx({
              width: W, height: 34, backgroundColor: '#4054A8',
              border: 'none', borderRadius: 3, color: '#BCC8E0',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.02em',
              transition: 'background-color 120ms', marginTop: 4,
            }, 'ds-btn ds-btn--primary pub-btn')}
            onMouseEnter={(e) => { if (!v2) e.currentTarget.style.backgroundColor = '#4A61C0'; }}
            onMouseLeave={(e) => { if (!v2) e.currentTarget.style.backgroundColor = '#4054A8'; }}
          >
            {v2 ? t('auth.setPasswordBtn', 'Set password') : t('auth.setPasswordButton', 'Set password')}
          </button>

          <button
            type="button"
            onClick={logout}
            {...sx({
              background: 'none', border: 'none',
              fontSize: 11, color: '#334155', cursor: 'pointer',
              fontFamily: 'inherit', marginTop: 4,
            }, 'pub-ghost')}
          >
            {t('user.signOut', 'Sign out')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
